import { useEffect, useState } from 'react';
import { Modal, Box, Typography, styled, TextField, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { imgUrl } from '../Helpers/utils/Constant';

export const getFullImageUrl = (url?: string | null) => {
  if (!url) return '';
  if (
    url.startsWith('blob:') ||
    url.startsWith('http://') ||
    url.startsWith('https://')
  ) {
    return url;
  }
  return url.startsWith('/') ? `${imgUrl}${url}` : `${imgUrl}/${url}`;
};

export const isPdfFile = (url?: string | null) => {
  if (!url) return false;
  const cleanUrl = url.split('?')[0];
  const ext = cleanUrl.split('.').pop()?.toLowerCase();
  return ext === 'pdf';
};

function ImageUploaderWithPreview({
  register,
  imgSerUrl,
  label,
  fileName,
  required,
  setError,
  clearErrors,
  errors,
  maxLabelLength = 50,
}: {
  imgSerUrl?: string | undefined | null;
  register: any;
  label: string;
  fileName: string;
  required?: boolean;
  setError: any;
  clearErrors: any;
  errors?: any;
  maxLabelLength?: number;
}) {
  const [errorMessage, setErrorMessage] = useState<{
    type: string;
    errorMessage: string;
  }>({
    type: '',
    errorMessage: '',
  });
  const [imageUrl, setImageUrl] = useState<string | null>(
    imgSerUrl ? (isPdfFile(imgSerUrl) ? 'pdf' : imgSerUrl) : null
  );
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (imgSerUrl) {
      setImageUrl(isPdfFile(imgSerUrl) ? 'pdf' : imgSerUrl);
    } else {
      setImageUrl(null);
    }
  }, [imgSerUrl]);

  const handleImageUpload = (event: any) => {
    const file = event.target.files?.[0];
    const MAX_SIZE_MB = 5;

    if (file) {
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > MAX_SIZE_MB) {
        setError(fileName, {
          type: 'custom',
          message: `File size exceeds ${MAX_SIZE_MB} MB.`,
        });
        setErrorMessage({
          errorMessage: `File size exceeds ${MAX_SIZE_MB} MB.`,
          type: fileName,
        });
        return;
      } else {
        clearErrors(fileName);
        setErrorMessage({ errorMessage: '', type: fileName });
        const ext = file.name.split('.').pop()?.toLowerCase();
        if (ext === 'pdf') {
          setImageUrl('pdf');
        } else {
          const url = URL.createObjectURL(file);
          setImageUrl(url);
        }
      }
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const hasError = !!(errorMessage?.errorMessage || errors?.[fileName]);

  return (
    <div>
      <Box sx={{ position: 'relative' }}>
        <input
          id={fileName}
          type='file'
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            opacity: 0,
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
          }}
          {...register(fileName, {
            validate: (fileList: any) => {
              if (required && !imgSerUrl && !imageUrl) {
                const hasFile =
                  fileList &&
                  (fileList.length > 0 ||
                    (typeof File !== 'undefined' && fileList instanceof File));
                if (!hasFile) {
                  return `${label || 'File'} is required.`;
                }
              }
              return true;
            },
            onChange: (event: any) => handleImageUpload(event),
          })}
        />
        <Box
          sx={{
            position: 'relative',
            border: `1px solid ${hasError ? '#d32f2f' : '#ccc'}`,
            borderRadius: '10px',
            padding: '14.5px',
            display: 'flex',
            alignItems: 'center',
            columnGap: '4px',
            cursor: 'pointer',
            '&:hover': {
              borderColor: hasError ? '#d32f2f' : '#8d98af',
            },
          }}
        >
          <legend
            style={{
              fontSize: '10px',
              padding: '0 6px',
              position: 'absolute',
              top: '-8px',
              left: '10px',
              backgroundColor: '#fff',
              color: hasError ? '#d32f2f' : 'inherit',
              maxWidth: '85%',
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              zIndex: 1,
            }}
          >
            <Tooltip title={label || ''} arrow placement='top'>
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                }}
              >
                {label?.length > maxLabelLength
                  ? `${label.slice(0, maxLabelLength)}...`
                  : label}
              </span>
            </Tooltip>
            {required && (
              <span style={{ color: 'red', marginLeft: '3px', flexShrink: 0 }}>
                *
              </span>
            )}
          </legend>
          <label htmlFor={fileName} style={{ cursor: 'pointer' }}>
            <Box
              sx={{
                border: '1px solid gray',
                padding: '0 6px',
                color: 'black',
                fontFamily: 'sans-serif',
                backgroundColor: '#F0F0F0',
                letterSpacing: '.5px',
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              {imageUrl?.startsWith('blob:')
                ? 'Uploaded File'
                : imgSerUrl
                  ? 'Change File'
                  : 'Choose File'}
            </Box>
          </label>
          {imgSerUrl || imageUrl?.startsWith('blob:') || imageUrl === 'pdf' ? (
            imageUrl === 'pdf' ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  ml: 0.5,
                  color: '#d32f2f',
                }}
              >

                <Typography sx={{ fontSize: '13px', color: 'inherit' }}>
                  1 file <PictureAsPdfIcon sx={{ fontSize: '18px', color: '#d32f2f' }} />
                </Typography>
              </Box>
            ) : (
              <Typography
                onClick={handleOpen}
                sx={{
                  cursor: 'pointer',
                  fontSize: '13px',
                  ml: 0.5,
                  color: 'primary.main',
                }}
              >
                1 file Img
              </Typography>
            )
          ) : (
            <Typography
              sx={{ fontSize: '13px', color: 'text.secondary', ml: 0.5 }}
            >
              No file chosen
            </Typography>
          )}
        </Box>
        {errorMessage?.errorMessage ? (
          <Box sx={{ color: '#d32f2f', fontSize: '12px', mt: 0.5, ml: 1 }}>
            {errorMessage.errorMessage}
          </Box>
        ) : errors?.[fileName]?.message ? (
          <Box sx={{ color: '#d32f2f', fontSize: '12px', mt: 0.5, ml: 1 }}>
            {String(errors[fileName]?.message)}
          </Box>
        ) : null}
        {imageUrl && imageUrl !== 'pdf' ? (
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              width: 40,
              height: 40,
              border: '1px solid #ccc',
              borderRadius: 2,
              overflow: 'hidden',
              position: 'absolute',
              right: 10,
              top: -12,
            }}
            onClick={handleOpen}
          >
            <img
              src={getFullImageUrl(imageUrl)}
              alt='Uploaded preview'
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        ) : (
          ''
        )}
      </Box>

      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 1,
            maxWidth: '90%',
            maxHeight: '90%',
            overflow: 'auto',
            borderRadius: '5px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'end' }}>
            <CloseIcon
              sx={{ cursor: 'pointer' }}
              onClick={() => setOpen(false)}
            />
          </Box>
          {imageUrl && (
            <img
              src={getFullImageUrl(imageUrl)}
              alt='Full-size preview'
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        </Box>
      </Modal>
    </div>
  );
}

export default ImageUploaderWithPreview;
export const CssTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    '&.fieldset': {
      backgroundColor: 'white',
      color: 'black',
    },
  },
  '.css-1d3z3hw-MuiOutlinedInput-notchedOutline': {
    borderRadius: '10px',
  },

  asterisk: {
    color: '#db3131',
    '&$error': {
      color: '#db3131',
    },
  },
});
